import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { ESearchStorageKey } from '../../../enums';
import { IAppState, IAuthState } from '../../../interfaces';
import { IOfferRequestsSearchOptions } from '../../../store/actions/offerRequestActions';
import { ETranslation } from '../../../translations/translation-keys';
import OperatorSearch from '../../Searches/OperatorSearch/OperatorSearch';
import Select from '../../UI/Select/Select';
import { useServiceOptions } from '../../../hooks/useServiceOptions';
import { municipalityOptions, provinceOptions } from '../../../config/forms';

interface IProps {
  onSearch: (search: IOfferRequestsSearchOptions) => void;
}

const OfferRequestsSearch: React.FC<IProps> = ({ onSearch }) => {
  const [isInit, setInit] = useState(false);
  const [operatorId, setOperatorId] = useState("");
  const [serviceIds, setServiceIds] = useState<string[]>([]);
  const [provinces, setProvinces] = useState<string[]>([]);
  const [municipalities, setMunicipalities] = useState<string[]>([]);
  const [city, setCity] = useState("");
  const [maxDistance, setMaxDistance] = useState<string>("");
  const [showArchived, setShowArchived] = useState(false);
  const [limit, setLimit] = useState(true);

  const { authUser } = useSelector<IAppState, IAuthState>((state) => state.auth);
  const { t } = useTranslation();
  const { serviceOptions, servicesLoading } = useServiceOptions();

  useEffect(() => {
    setOperatorId(localStorage.getItem(ESearchStorageKey.OFFER_REQUEST_OPERATOR_ID) || '');

    if (authUser?.isServiceProvider) {
      const orgServices = authUser.organization?.services?.filter((s) => !s.archived) || [];
      const allOrgServiceIds = orgServices
        .map((s) => s.id?.toString())
        .filter((id): id is string => Boolean(id));

      const excludedStorageKey = authUser.organization?.id
        ? `${ESearchStorageKey.OFFER_REQUEST_EXCLUDED_SERVICE_IDS}_${authUser.organization.id}`
        : ESearchStorageKey.OFFER_REQUEST_EXCLUDED_SERVICE_IDS;

      const storedExcluded = localStorage.getItem(excludedStorageKey);
      if (storedExcluded !== null) {
        try {
          const excludedIds: string[] = JSON.parse(storedExcluded);
          // Valitaan kaikki organisaation palvelut paitsi ne, jotka käyttäjä on ottanut rastilla pois
          setServiceIds(allOrgServiceIds.filter((id) => !excludedIds.includes(id)));
        } catch {
          setServiceIds(allOrgServiceIds);
        }
      } else {
        // Ensimmäinen kerta tai migraation jälkeen (ei vielä poissulkuja tallennettuna):
        // Valitaan automaattisesti kaikki organisaation palvelut (myös vasta migroitu Hanki Energia-avustus!)
        setServiceIds(allOrgServiceIds);
      }
      // Poistetaan vanha yksittäisen palvelun avain ristiriitojen estämiseksi
      localStorage.removeItem(ESearchStorageKey.OFFER_REQUEST_SERVICE_ID);
    } else {
      // Pääkäyttäjä / Operaattori: luetaan tallennetut monivalintapalvelut tai vanha yksittäinen avain
      const storedAdminServiceIds = localStorage.getItem(ESearchStorageKey.OFFER_REQUEST_SERVICE_IDS);
      if (storedAdminServiceIds) {
        try {
          setServiceIds(JSON.parse(storedAdminServiceIds));
        } catch {
          setServiceIds([]);
        }
      } else {
        const prevServiceId = localStorage.getItem(ESearchStorageKey.OFFER_REQUEST_SERVICE_ID) || '';
        if (prevServiceId) {
          setServiceIds([prevServiceId]);
        }
      }
    }

    if (authUser?.organization?.operatingRadius && !maxDistance) {
      setMaxDistance(authUser.organization.operatingRadius.toString());
    }
    setInit(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser]);

  const handleServiceChange = (val: any) => {
    const newServiceIds = (Array.isArray(val) ? val : val ? [val] : []).map(String);
    setServiceIds(newServiceIds);

    if (authUser?.isServiceProvider) {
      const orgServices = authUser.organization?.services?.filter((s) => !s.archived) || [];
      const allOrgServiceIds = orgServices
        .map((s) => s.id?.toString())
        .filter((id): id is string => Boolean(id));

      const excludedStorageKey = authUser.organization?.id
        ? `${ESearchStorageKey.OFFER_REQUEST_EXCLUDED_SERVICE_IDS}_${authUser.organization.id}`
        : ESearchStorageKey.OFFER_REQUEST_EXCLUDED_SERVICE_IDS;

      // Kaikki organisaation palvelut, joita ei löydy uudesta valinnasta, merkitään poissuljetuiksi
      const excluded = allOrgServiceIds.filter((id) => !newServiceIds.includes(id));
      localStorage.setItem(excludedStorageKey, JSON.stringify(excluded));
    } else {
      localStorage.setItem(ESearchStorageKey.OFFER_REQUEST_SERVICE_IDS, JSON.stringify(newServiceIds));
    }
  };

  useEffect(() => {
    if (isInit) {
      onSearch({ 
        operatorId, 
        limit,
        serviceIds,
        provinces,
        municipalities,
        municipality: municipalities.length === 1 ? municipalities[0] : undefined,
        city,
        maxDistance,
        showArchived
      });
      localStorage.setItem(ESearchStorageKey.OFFER_REQUEST_OPERATOR_ID, operatorId);
    }
  }, [operatorId, limit, serviceIds, provinces, municipalities, city, maxDistance, showArchived, onSearch, isInit]);

  const userOperatingRadius = authUser?.organization?.operatingRadius;

  return (
    <div style={{
      backgroundColor: '#ffffff',
      border: '1px solid #e2e8f0',
      borderRadius: '12px',
      padding: '1.5rem',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05), 0 1px 2px 0 rgba(0, 0, 0, 0.03)',
      marginBottom: '1.5rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '1.25rem'
    }}>
      <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: '#1e293b' }}>
        Suodata tarjouspyyntöjä
      </h3>
      
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: '1.25rem',
        alignItems: 'end'
      }}>
        {/* Operator Search (for Primary admin) */}
        {authUser?.isPrimary && (
          <div>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, color: '#475569', marginBottom: '0.5rem' }}>
              Operaattori
            </label>
            <OperatorSearch operatorId={operatorId} setOperatorId={setOperatorId} />
          </div>
        )}

        {/* Multi-select Services */}
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, color: '#475569', marginBottom: '0.5rem' }}>
            {t(ETranslation.COMMON_SERVICE)}
          </label>
          <Select
            inputName="serviceSearch"
            value={serviceIds}
            onChange={handleServiceChange}
            options={serviceOptions}
            isSearchable
            isClearable
            isMulti
            isLoading={servicesLoading}
            placeholder="Kaikki palvelut"
          />
        </div>

        {/* Multi-select Province (Maakunta) */}
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, color: '#475569', marginBottom: '0.5rem' }}>
            {t(ETranslation.OFFER_REQUEST_PROVINCE)}
          </label>
          <Select
            inputName="provinceSearch"
            value={provinces}
            onChange={(val) => setProvinces(val as string[])}
            options={provinceOptions}
            isSearchable
            isClearable
            isMulti
            placeholder="Kaikki maakunnat"
          />
        </div>

        {/* Multi-select Municipality (Kunta) */}
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, color: '#475569', marginBottom: '0.5rem' }}>
            {t(ETranslation.OFFER_REQUEST_MUNICIPALITY)}
          </label>
          <Select
            inputName="municipalitySearch"
            value={municipalities}
            onChange={(val) => setMunicipalities(val as string[])}
            options={municipalityOptions}
            isSearchable
            isClearable
            isMulti
            placeholder="Kaikki kunnat"
          />
        </div>

        {/* Paikkakunta Text Search */}
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, color: '#475569', marginBottom: '0.5rem' }}>
            Paikkakunta
          </label>
          <input
            type="text"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="Hae paikkakuntaa..."
            style={{
              width: '100%',
              height: '38px',
              padding: '0.375rem 0.75rem',
              fontSize: '0.9rem',
              fontWeight: 400,
              lineHeight: '1.5',
              color: '#2d3748',
              backgroundColor: '#fff',
              border: '1px solid #cbd5e1',
              borderRadius: '0.375rem',
              transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
              boxSizing: 'border-box'
            }}
          />
        </div>

        {/* Max Distance (km) - Optional Filter */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, color: '#475569', margin: 0 }}>
              Max etäisyys (km)
            </label>
            {maxDistance ? (
              <button
                type="button"
                onClick={() => setMaxDistance("")}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#2563eb',
                  fontSize: '0.75rem',
                  cursor: 'pointer',
                  padding: 0,
                  textDecoration: 'underline'
                }}
              >
                Näytä kaikki
              </button>
            ) : userOperatingRadius ? (
              <button
                type="button"
                onClick={() => setMaxDistance(userOperatingRadius.toString())}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#2563eb',
                  fontSize: '0.75rem',
                  cursor: 'pointer',
                  padding: 0,
                  textDecoration: 'underline'
                }}
              >
                Oma säde ({userOperatingRadius} km)
              </button>
            ) : null}
          </div>
          <input
            type="number"
            value={maxDistance}
            onChange={(e) => setMaxDistance(e.target.value)}
            placeholder="Ei rajaa (kaikki)"
            min="0"
            style={{
              width: '100%',
              height: '38px',
              padding: '0.375rem 0.75rem',
              fontSize: '0.9rem',
              fontWeight: 400,
              lineHeight: '1.5',
              color: '#2d3748',
              backgroundColor: '#fff',
              border: '1px solid #cbd5e1',
              borderRadius: '0.375rem',
              transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
              boxSizing: 'border-box'
            }}
          />
        </div>
      </div>

      {/* Status Checkboxes and Load All link */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: '0.75rem',
        borderTop: '1px solid #f1f5f9',
        gap: '1rem'
      }}>
        <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem', color: '#334155', userSelect: 'none' }}>
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
            />
            Näytä arkistoidut
          </label>
        </div>

        {!authUser?.isServiceProvider && limit && (
          <div 
            style={{ 
              fontSize: '0.9rem', 
              color: '#2563eb', 
              cursor: 'pointer', 
              textDecoration: 'underline',
              fontWeight: 500
            }} 
            onClick={() => setLimit(false)}
          >
            {t(ETranslation.COMMON_LOAD_ALL)}
          </div>
        )}
        {!limit && (
          <div style={{ fontSize: '0.85rem', color: '#64748b', fontStyle: 'italic' }}>
            Kaikki tarjouspyynnöt ladattu
          </div>
        )}
      </div>
    </div>
  );
};

export default OfferRequestsSearch;
